import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { DeadlineBadge } from "@/components/DeadlineBadge";
import { OpsCommandCenter } from "@/components/OpsCommandCenter";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Briefcase, Clock, FileCheck2, DollarSign, Users, UserCog, TrendingUp, MapPin, AlertTriangle, Zap, Stamp, Mail } from "lucide-react";
import { LoadtestCleanupButton } from "@/components/admin/LoadtestCleanupButton";
import { CoverBackfillButton } from "@/components/admin/CoverBackfillButton";
import { RefundsDue } from "@/components/admin/RefundsDue";
import { SendAttemptEmailButton } from "@/components/admin/SendAttemptEmailButton";

function Stat({ icon: Icon, label, value, tone, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={!onClick}
      className={`w-full rounded-xl border border-slate-200 bg-white p-5 text-left ${onClick ? "cursor-pointer transition hover:border-slate-300 hover:shadow-sm" : ""}`}
    >
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
      <p className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{value}</p>
      <p className="mt-0.5 text-sm text-slate-500">{label}{onClick && " ›"}</p>
    </button>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [ops, setOps] = useState(null);
  const [orders, setOrders] = useState([]);
  const [calc, setCalc] = useState(null);
  const [emailMode, setEmailMode] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/ops-summary").then(({ data }) => setOps(data)).catch(() => {});
    api.get("/admin/jobs", { params: { risk: "at_risk", page: 1, page_size: 8 } }).then(({ data }) => setOrders(data.items || [])).catch(() => {});
    api.get("/admin/email-mode").then(({ data }) => setEmailMode(data)).catch(() => {});
  }, []);

  const serveRate = stats && stats.total_attempts ? Math.round((stats.served_attempts / stats.total_attempts) * 100) : 0;
  const activeJobs = orders;
  const needsAttentionCount = ops ? ops.needs_attention.at_risk_jobs : 0;
  const needsAttention = orders;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Audit command center</h2>
        <p className="text-slate-500">Monitor every active job, attempt, and proof across the platform.</p>
      </div>
      <LoadtestCleanupButton />
      <CoverBackfillButton />
      <RefundsDue />
      <SendAttemptEmailButton />

      {emailMode && (
        <div
          data-testid="admin-email-mode"
          className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${emailMode.is_production ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
        >
          <div className="flex items-center gap-2">
            <Mail className={`h-4 w-4 ${emailMode.is_production ? "text-emerald-600" : "text-amber-600"}`} />
            <span className={`text-sm font-semibold ${emailMode.is_production ? "text-emerald-800" : "text-amber-800"}`} data-testid="email-mode-label">
              {emailMode.is_production ? "Email: Production sending" : "Email: Non-prod capture mode"}
            </span>
            <span className="rounded-md bg-white/70 px-2 py-0.5 text-[11px] font-mono text-slate-500">APP_ENV={emailMode.app_env || "unset"}</span>
          </div>
          <div className="text-xs font-medium text-slate-600" data-testid="email-mode-allowlist">
            {emailMode.is_production
              ? "Live email is being delivered to recipients."
              : `Captured/logged — only ${emailMode.allowlist_count} allowlisted test address${emailMode.allowlist_count === 1 ? "" : "es"} receive mail.`}
          </div>
          {(() => {
            const c = emailMode.config || {};
            const missing = [["legal name", "company_legal_name"], ["mailing address", "mailing_address"], ["support email", "support_email"], ["unsubscribe secret", "unsub_secret"]].filter(([, k]) => !c[k]).map(([l]) => l);
            return missing.length ? (
              <div className="w-full border-t border-amber-200/70 pt-2 text-[11px] font-medium text-amber-700" data-testid="email-mode-config-missing">
                Before production sending, set: {missing.join(", ")}.
              </div>
            ) : null;
          })()}
        </div>
      )}

      {ops?.review_queue && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="admin-priority-actions">
          <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-5" data-testid="card-urgent-service-requests">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-rose-700">Urgent service requests</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-rose-900" data-testid="urgent-service-requests-count">{ops.review_queue.urgent_service_requests ?? 0}</p>
              </div>
              <Zap className="h-8 w-8 text-rose-400" />
            </div>
            <button data-testid="review-urgent-service-btn" onClick={() => nav("/admin/jobs?stage=urgent_service")} className="mt-3 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700">Review urgent requests ›</button>
          </div>
          <div className="rounded-xl border-2 border-violet-300 bg-violet-50 p-5" data-testid="card-expedited-payout-requests">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-violet-700">Expedited payout requests</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-violet-900" data-testid="expedited-payout-requests-count">{ops.review_queue.expedited_payout_requests ?? 0}</p>
              </div>
              <DollarSign className="h-8 w-8 text-violet-400" />
            </div>
            <button data-testid="process-expedited-payouts-btn" onClick={() => nav("/admin/payouts?filter=processing")} className="mt-3 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-700">Process expedited payouts ›</button>
          </div>
        </div>
      )}

      {ops?.review_queue && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-review-queue">
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5" data-testid="card-new-clients-review">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-indigo-700">New clients awaiting review</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-indigo-900" data-testid="new-clients-count">{ops.review_queue.new_clients}</p>
              </div>
              <Users className="h-8 w-8 text-indigo-400" />
            </div>
            <button data-testid="review-new-clients-btn" onClick={() => nav("/admin/clients?status=pending_approval")} className="mt-3 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700">Review new clients ›</button>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="card-new-assignments-review">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">New assignments awaiting approval</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-amber-900" data-testid="new-assignments-count">{ops.review_queue.new_assignments}</p>
              </div>
              <Briefcase className="h-8 w-8 text-amber-400" />
            </div>
            <button data-testid="review-assignments-btn" onClick={() => nav("/admin/jobs?stage=awaiting_approval")} className="mt-3 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700">Review assignments ›</button>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="card-service-completion-review">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Service completion awaiting admin review</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-emerald-900" data-testid="service-completion-review-count">{ops.review_queue.service_completion_review ?? 0}</p>
              </div>
              <FileCheck2 className="h-8 w-8 text-emerald-400" />
            </div>
            <button data-testid="review-service-completion-btn" onClick={() => nav("/admin/jobs?stage=pending_review")} className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700">Review completed services ›</button>
          </div>
          <div className="rounded-xl border-2 border-indigo-300 bg-indigo-50 p-5" data-testid="card-paid-pending-review">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-indigo-700">Paid — Pending Admin Review</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-indigo-900" data-testid="paid-pending-review-count">{ops.review_queue.paid_pending_review ?? 0}</p>
              </div>
              <DollarSign className="h-8 w-8 text-indigo-400" />
            </div>
            <button data-testid="review-paid-orders-btn" onClick={() => nav("/admin/jobs?stage=paid_pending_review")} className="mt-3 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700">Review paid orders ›</button>
          </div>
          <div className="rounded-xl border-2 border-teal-300 bg-teal-50 p-5" data-testid="card-paid-awaiting-assignment">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-teal-700">Paid orders awaiting assignment</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-teal-900" data-testid="paid-awaiting-assignment-count">{ops.review_queue.paid_awaiting_assignment ?? 0}</p>
              </div>
              <DollarSign className="h-8 w-8 text-teal-400" />
            </div>
            <button data-testid="assign-paid-orders-btn" onClick={() => nav("/admin/jobs?stage=paid_awaiting_assignment")} className="mt-3 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700">Assign paid orders ›</button>
          </div>
          <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-5" data-testid="card-new-notary-requests">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-cyan-700">New notary requests</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-cyan-900" data-testid="new-notary-requests-count">{ops.review_queue.new_notary_requests ?? 0}</p>
              </div>
              <Stamp className="h-8 w-8 text-cyan-400" />
            </div>
            <button data-testid="review-new-notary-btn" onClick={() => nav("/admin/notary?filter=new")} className="mt-3 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700">Review new notary requests ›</button>
          </div>
        </div>
      )}


      <div data-testid="needs-attention-section" className="rounded-xl border border-red-200 bg-white">
        <div className="flex items-center justify-between border-b border-red-100 bg-red-50/50 px-5 py-4">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-red-900">
            <AlertTriangle className="h-5 w-5 text-red-600" /> Needs attention <span className="text-sm font-normal text-red-500">(at-risk jobs: passed + urgent + approaching)</span>
          </h3>
          <div className="flex items-center gap-3">
            <span data-testid="needs-attention-count" className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">{needsAttentionCount}</span>
            <button data-testid="needs-attention-view-all" onClick={() => nav("/admin/jobs?risk=at_risk")} className="text-sm font-semibold text-red-600 hover:underline">View all {needsAttentionCount} →</button>
          </div>
        </div>
        {needsAttention.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-slate-400">No deadline risks — everything is on track.</p>
        ) : (
          <>
            <p className="px-5 pt-3 text-xs font-medium uppercase tracking-wide text-slate-400">Top priority (preview) — click “View all” for the full population</p>
            <div className="divide-y divide-slate-100">
              {needsAttention.map((o) => (
                <button key={o.id} data-testid={`attention-row-${o.job_number}`} onClick={() => nav(`/admin/jobs/${o.id}`)} className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition hover:bg-red-50/40">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-arps text-sm font-semibold text-slate-900">#{o.job_number}</span>
                      <span className="text-xs text-slate-400">{o.assigned_server_name || "Unassigned"}</span>
                    </div>
                    <p className="mt-0.5 truncate text-sm text-slate-600">{o.defendant} · {o.service_address?.city}, {o.service_address?.state}</p>
                  </div>
                  <DeadlineBadge status={o.deadline_status} days={o.days_to_deadline} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <OpsCommandCenter />

      {stats && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat icon={Briefcase} label="Total jobs" value={stats.total_orders} tone="bg-slate-100 text-slate-700" onClick={() => nav("/admin/jobs?stage=all")} testid="stat-total-jobs" />
            <Stat icon={Clock} label="Current active work" value={stats.current_active_work} tone="bg-sky-100 text-sky-700" onClick={() => nav("/admin/jobs?stage=active_work")} testid="stat-active" />
            <Stat icon={FileCheck2} label="Pending review" value={stats.pending_review} tone="bg-amber-100 text-amber-700" onClick={() => nav("/admin/jobs?stage=active")} testid="stat-pending-review" />
            <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue.toLocaleString()}`} tone="bg-emerald-100 text-emerald-700" onClick={() => nav("/admin/billing")} testid="stat-revenue" />
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat icon={Users} label="Clients" value={stats.clients} tone="bg-violet-100 text-violet-700" onClick={() => nav("/admin/clients")} testid="stat-clients" />
            <Stat icon={UserCog} label="Providers" value={stats.servers} tone="bg-indigo-100 text-indigo-700" onClick={() => nav("/admin/servers")} testid="stat-servers" />
            <Stat icon={MapPin} label="Attempts logged" value={stats.total_attempts} tone="bg-rose-100 text-rose-700" onClick={() => setCalc("attempts")} testid="stat-attempts" />
            <Stat icon={TrendingUp} label="Serve rate" value={`${serveRate}%`} tone="bg-emerald-100 text-emerald-700" onClick={() => setCalc("serve")} testid="stat-serve-rate" />
          </div>
        </>
      )}

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="font-display text-lg font-bold text-slate-900">At-risk jobs <span className="text-sm font-normal text-slate-400">(top priority preview)</span></h3>
          <button data-testid="view-all-jobs" onClick={() => nav("/admin/jobs?risk=at_risk")} className="text-sm font-semibold text-emerald-600 hover:underline">View all {needsAttentionCount} →</button>
        </div>
        {activeJobs.length === 0 ? <p className="px-5 py-10 text-center text-sm text-slate-400">No at-risk jobs.</p> : (
          <div className="divide-y divide-slate-100">
            {activeJobs.map((o) => (
              <button key={o.id} data-testid={`admin-job-row-${o.job_number}`} onClick={() => nav(`/admin/jobs/${o.id}`)} className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-arps text-sm font-semibold text-slate-900">#{o.job_number}</span>
                    <span className="text-xs text-slate-400">{o.assigned_server_name || "Unassigned"}</span>
                  </div>
                  <p className="mt-0.5 truncate text-sm text-slate-600">{o.defendant} · {o.service_address?.city}, {o.service_address?.state}</p>
                </div>
                <StatusBadge status={o.status} pulse={["in_progress", "assigned"].includes(o.status)} />
              </button>
            ))}
          </div>
        )}
      </div>

      {calc && stats && (
        <Dialog open={!!calc} onOpenChange={(v) => !v && setCalc(null)}>
          <DialogContent data-testid="metric-calc-dialog">
            <DialogHeader><DialogTitle>{calc === "serve" ? "Serve rate — calculation" : "Attempts logged — breakdown"}</DialogTitle></DialogHeader>
            {calc === "serve" ? (
              <div className="space-y-2 text-sm">
                <p className="text-slate-600">Authoritative formula (from persisted attempt records):</p>
                <div className="rounded-lg bg-slate-50 p-3 font-mono-arps text-slate-900">
                  {stats.serve_rate.pct}% = {stats.serve_rate.numerator} served ÷ {stats.serve_rate.denominator} total × 100
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div data-testid="calc-served" className="rounded-lg border border-slate-100 p-2 text-center"><p className="text-xl font-bold text-emerald-600">{stats.attempts_breakdown.served}</p><p className="text-xs text-slate-500">Served</p></div>
                  <div data-testid="calc-notserved" className="rounded-lg border border-slate-100 p-2 text-center"><p className="text-xl font-bold text-slate-700">{stats.attempts_breakdown.not_served}</p><p className="text-xs text-slate-500">Not served</p></div>
                  <div data-testid="calc-total" className="rounded-lg border border-slate-100 p-2 text-center"><p className="text-xl font-bold text-slate-900">{stats.attempts_breakdown.total}</p><p className="text-xs text-slate-500">Total</p></div>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <p className="text-slate-600">Every count is an authoritative attempt record ({stats.attempts_breakdown.total} total).</p>
                <div className="grid grid-cols-3 gap-2">
                  <div data-testid="calc-served" className="rounded-lg border border-slate-100 p-3 text-center"><p className="text-2xl font-bold text-emerald-600">{stats.attempts_breakdown.served}</p><p className="text-xs text-slate-500">Served</p></div>
                  <div data-testid="calc-notserved" className="rounded-lg border border-slate-100 p-3 text-center"><p className="text-2xl font-bold text-slate-700">{stats.attempts_breakdown.not_served}</p><p className="text-xs text-slate-500">Not served</p></div>
                  <div data-testid="calc-total" className="rounded-lg border border-slate-100 p-3 text-center"><p className="text-2xl font-bold text-slate-900">{stats.attempts_breakdown.total}</p><p className="text-xs text-slate-500">Total</p></div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
